import { Capacitor } from "@capacitor/core";
import type { SupportedStorage } from "@supabase/supabase-js";
import { env } from "@config/env";
import { getPlatformStorage } from "@lib/platform/storage";

/** Mirrors GoTrue's default key: `sb-<project-ref>-auth-token` (supabase-js 2.x). */
export function getSupabaseAuthStorageKey(): string {
  const hostname = new URL(env.supabaseUrl()).hostname;
  const projectRef = hostname.split(".")[0] ?? hostname;
  return `sb-${projectRef}-auth-token`;
}

function webAuthStorage(): SupportedStorage {
  return {
    getItem(key) {
      if (typeof window === "undefined") return null;
      return window.localStorage.getItem(key);
    },
    setItem(key, value) {
      window.localStorage.setItem(key, value);
    },
    removeItem(key) {
      window.localStorage.removeItem(key);
    },
  };
}

function nativeAuthStorage(): SupportedStorage {
  return {
    async getItem(key) {
      const storage = getPlatformStorage();
      let value = await storage.get(key);

      if (value === null) {
        // One-time migration from the legacy WKWebView origin storage.
        try {
          const legacy = window.localStorage.getItem(key);
          if (legacy !== null) {
            await storage.set(key, legacy);
            window.localStorage.removeItem(key);
            value = legacy;
          }
        } catch {
          // Best-effort; next read simply misses.
        }
      }

      return value;
    },
    async setItem(key, value) {
      await getPlatformStorage().set(key, value);
    },
    async removeItem(key) {
      await getPlatformStorage().remove(key);
    },
  };
}

/**
 * Storage backend for the Supabase auth session.
 * Web: `window.localStorage` (byte-for-byte the pre-navigation default).
 * Native: Capacitor Preferences, resolved directly so early auth reads do not
 * depend on PlatformProvider's asynchronous adapter swap.
 */
export function getSupabaseAuthStorage(): SupportedStorage {
  return Capacitor.isNativePlatform() ? nativeAuthStorage() : webAuthStorage();
}